import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Modal } from '@mui/material';
import { NoticeBanner, NoticeBannerPayload } from '~/api/types';
import { useCreateNoticeBannerMutation, useEditNoticeBannerMutation } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { localDateTimeInputToUtc, utcToLocalDateTimeInput } from '~/utils';
import { noticeBannerSchema } from '~/validation';
import { NoticeBannerForm } from '.';

export interface NoticeBannerModalProps {
  payload: {
    id: string;
    bannerData: NoticeBanner;
  };
}

const NoticeBannerModal: FC<NoticeBannerModalProps> = ({ payload: { id, bannerData } }) => {
  const dispatch = useAppDispatch();
  const [createBanner, { isLoading }] = useCreateNoticeBannerMutation({});
  const [editBanner, { isLoading: isEditing }] = useEditNoticeBannerMutation({});

  const handleClose = () => {
    if (!isLoading && !isEditing) dispatch(closeModalById(id));
  };

  const formMethods = useForm<NoticeBannerPayload>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(noticeBannerSchema(!!bannerData)),
    defaultValues: {
      message: bannerData?.message,
      start_datetime: utcToLocalDateTimeInput(bannerData?.start_datetime),
      end_datetime: utcToLocalDateTimeInput(bannerData?.end_datetime),
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = formMethods;

  const onSubmit = async (data: NoticeBannerPayload) => {
    const payload: NoticeBannerPayload = {
      message: data.message,
      start_datetime: localDateTimeInputToUtc(data.start_datetime),
      end_datetime: localDateTimeInputToUtc(data.end_datetime),
    };

    if (bannerData) {
      await editBanner({ bannerId: bannerData.id, ...payload });
    } else {
      await createBanner(payload);
    }
    handleClose();
  };

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader onClose={handleClose} title={[`${bannerData ? 'Edit' : 'Add'} Notice Banner`]} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <NoticeBannerForm isEdit={!!bannerData} />
            <Box sx={modalActionBlockStyles}>
              <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
              <CustomButton
                label={bannerData ? 'Save' : 'Add'}
                type="submit"
                disabled={!isDirty || !isValid || isLoading}
              />
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default NoticeBannerModal;
